/**
 * Many policies from many files.
 *
 * A queue, not a loop over the single upload. Each file becomes its own row
 * with its own title, its own check, its own progress and its own outcome —
 * because the thing a person needs from a batch is not "did it work" but
 * "which ones did not, and why", and a loop that stops at the first failure
 * answers neither.
 *
 * WHAT IS SHARED with the single upload is exactly the part that must be:
 * uploadOnePolicy, the three-step pipeline. There is one copy of the steps
 * and this drives it once per file.
 *
 * WHAT IS CHECKED, and when. On arrival, each file gets the cheap checks
 * (type, size, empty) and a one-kilobyte probe — enough to catch a cloud
 * placeholder or a renamed Word file without holding a hundred files' bytes
 * in memory. The full read happens just before that file's own upload.
 *
 * TWO AT A TIME. Enough to overlap one file's transfer with the next one's
 * bookkeeping; few enough that a hundred-file batch is not a hundred
 * simultaneous connections to Cloud Storage from one laptop.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ConversionFailed,
  failureMessage,
  probeHead,
  readFully,
  rejectReason,
  uploadOnePolicy,
  type UploadStage,
} from './useUpload';
import { titleFromFileName } from './titleFromFileName';

/**
 * The most files one batch may hold.
 *
 * Not a technical ceiling — a person one. A hundred rows is already more than
 * anyone reviews title by title in one sitting, and a hospital onboarding its
 * library is better served by three batches it can check than one it cannot.
 */
export const MAX_BULK_FILES = 100;

export const BULK_CONCURRENCY = 2;

export const NEEDS_TITLE = 'Give this policy a title.';

export type RowStatus =
  | { kind: 'checking' }
  | { kind: 'ready' }
  /** Will not upload. The reason is shown; the row can be removed. */
  | { kind: 'problem'; reason: string }
  | { kind: 'creating' }
  | { kind: 'uploading'; percent: number }
  | { kind: 'attaching' }
  | { kind: 'converting' }
  | { kind: 'submitting' }
  | { kind: 'done'; policyId: string; note?: string }
  /** The upload was attempted and failed. Retryable. */
  | { kind: 'failed'; message: string };

export interface BulkRow {
  id: string;
  file: File;
  title: string;
  status: RowStatus;
}

export interface BulkSettings {
  reviewCycleMonths: number;
  requiresAcknowledgement: boolean;
  submitForReview: boolean;
  /** Filing applied to every file in the batch; the batch is one department's stack. */
  departmentIds?: string[];
  categoryId?: string;
  documentTypeId?: string;
  targetAudience?: 'all' | 'departments';
}

/** One row per distinct file. The same file chosen twice is the same row. */
export function rowKey(file: File): string {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

/**
 * The row id as something the server will accept as an id: the field is
 * bounded and pattern-checked, and a file name is neither.
 */
export function attemptIdFor(rowId: string): string {
  let h = 0;
  for (let i = 0; i < rowId.length; i++) h = (Math.imul(31, h) + rowId.charCodeAt(i)) | 0;
  return `up_${(h >>> 0).toString(36)}_${rowId.length.toString(36)}`;
}

function stageToStatus(stage: UploadStage): RowStatus | null {
  switch (stage.phase) {
    case 'creating':
      return { kind: 'creating' };
    case 'uploading':
      return { kind: 'uploading', percent: stage.percent };
    case 'attaching':
      return { kind: 'attaching' };
    case 'converting':
      return { kind: 'converting' };
    case 'submitting':
      return { kind: 'submitting' };
    default:
      // done/failed/idle are decided here from the promise, not from a stage.
      return null;
  }
}

export function useBulkUpload(orgId: string | null) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [running, setRunning] = useState(false);
  /**
   * A batch has been run to the end. Recorded here, not derived from the
   * rows: "nothing is ready" is also true of a list whose one row was just
   * sent back for a title, and that list must stay open for editing.
   */
  const [finished, setFinished] = useState(false);

  // The latest rows, readable from inside long-running async work without
  // capturing a stale closure.
  const rowsRef = useRef<BulkRow[]>([]);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // No state updates after unmount: a batch can outlive the page.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const patch = useCallback((id: string, changes: Partial<BulkRow>) => {
    if (!mounted.current) return;
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...changes } : r)));
  }, []);

  /**
   * Add files to the queue. Returns what happened to the ones that did not
   * make it, so the page can say "3 were already in the list" rather than
   * silently showing fewer rows than were dropped.
   */
  const addFiles = useCallback(
    (files: File[]) => {
      const existing = new Set(rowsRef.current.map((r) => r.id));
      const fresh: BulkRow[] = [];
      let duplicates = 0;

      for (const file of files) {
        const id = rowKey(file);
        if (existing.has(id)) {
          duplicates += 1;
          continue;
        }
        existing.add(id);

        const reason = rejectReason(file);
        fresh.push({
          id,
          file,
          title: titleFromFileName(file.name),
          status: reason ? { kind: 'problem', reason } : { kind: 'checking' },
        });
      }

      const room = MAX_BULK_FILES - rowsRef.current.length;
      const admitted = fresh.slice(0, Math.max(0, room));
      const overCap = fresh.length - admitted.length;

      if (admitted.length) {
        rowsRef.current = [...rowsRef.current, ...admitted];
        setRows(rowsRef.current);

        // The head probe, per file, in the background. Only rows still
        // "checking" take the verdict — a removed row is gone.
        for (const row of admitted) {
          if (row.status.kind !== 'checking') continue;
          void probeHead(row.file).then((result) => {
            const current = rowsRef.current.find((r) => r.id === row.id);
            if (!current || current.status.kind !== 'checking') return;
            patch(row.id, {
              status: result.ok ? { kind: 'ready' } : { kind: 'problem', reason: result.reason },
            });
          });
        }
      }

      return { added: admitted.length, duplicates, overCap };
    },
    [patch],
  );

  const removeRow = useCallback((id: string) => {
    rowsRef.current = rowsRef.current.filter((r) => r.id !== id);
    setRows(rowsRef.current);
  }, []);

  /**
   * Drop every row that cannot be uploaded as it is — wrong type, too big,
   * unreadable. A row that merely wants a title is not invalid; it stays.
   */
  const removeInvalid = useCallback(() => {
    rowsRef.current = rowsRef.current.filter(
      (r) => !(r.status.kind === 'problem' && r.status.reason !== NEEDS_TITLE),
    );
    setRows(rowsRef.current);
  }, []);

  const setTitle = useCallback((id: string, title: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        // A title arriving fixes the one problem a title can fix.
        const status: RowStatus =
          r.status.kind === 'problem' && r.status.reason === NEEDS_TITLE && title.trim()
            ? { kind: 'ready' }
            : r.status;
        return { ...r, title, status };
      }),
    );
  }, []);

  /** Upload one row, recording whatever happens to it. */
  const processOne = useCallback(
    async (id: string, settings: BulkSettings) => {
      const row = rowsRef.current.find((r) => r.id === id);
      if (!row || !orgId) return;

      try {
        patch(id, { status: { kind: 'creating' } });
        const bytes = await readFully(row.file);
        const policyId = await uploadOnePolicy(
          orgId,
          {
            title: row.title.trim(),
            reviewCycleMonths: settings.reviewCycleMonths,
            requiresAcknowledgement: settings.requiresAcknowledgement,
            submitForReview: settings.submitForReview,
            departmentIds: settings.departmentIds,
            categoryId: settings.categoryId,
            documentTypeId: settings.documentTypeId,
            targetAudience: settings.targetAudience,
            file: row.file,
            bytes,
            /**
             * The row's own id, which is the file's identity and does not
             * change when the row is retried. A retried row therefore finds
             * the policy its first attempt created instead of making a second
             * one with a second number (finding K).
             */
            attemptId: attemptIdFor(id),
          },
          (stage) => {
            const status = stageToStatus(stage);
            if (status) patch(id, { status });
          },
        );
        patch(id, { status: { kind: 'done', policyId } });
      } catch (e) {
        if (e instanceof ConversionFailed) {
          // The draft exists with its Word file; only the PDF is missing.
          patch(id, { status: { kind: 'done', policyId: e.policyId, note: e.message } });
          return;
        }
        // This row failed. The others carry on; that is the point of a queue.
        patch(id, { status: { kind: 'failed', message: failureMessage(e) } });
      }
    },
    [orgId, patch],
  );

  /** Run a set of rows, BULK_CONCURRENCY at a time, in order. */
  const run = useCallback(
    async (ids: string[], settings: BulkSettings) => {
      if (!ids.length) return;
      setRunning(true);
      setFinished(false);

      let next = 0;
      const worker = async () => {
        while (next < ids.length) {
          const id = ids[next++]!;
          await processOne(id, settings);
        }
      };
      await Promise.all(Array.from({ length: Math.min(BULK_CONCURRENCY, ids.length) }, worker));

      if (mounted.current) {
        setRunning(false);
        setFinished(true);
      }
      void queryClient.invalidateQueries({ queryKey: ['policies', orgId] });
    },
    [orgId, processOne, queryClient],
  );

  /**
   * Upload everything that is ready.
   *
   * A ready row with no title is not uploaded silently under an empty name;
   * it is marked so the person sees which ones need a word from them. Says
   * how many were sent and how many were sent back, so the page can put the
   * cursor where the work now is.
   */
  const start = useCallback(
    async (settings: BulkSettings) => {
      if (running) return { started: 0, needsTitle: 0 };

      const untitled = rowsRef.current.filter((r) => r.status.kind === 'ready' && !r.title.trim());
      if (untitled.length) {
        rowsRef.current = rowsRef.current.map((r) =>
          untitled.some((u) => u.id === r.id)
            ? { ...r, status: { kind: 'problem', reason: NEEDS_TITLE } }
            : r,
        );
        setRows(rowsRef.current);
      }

      const ids = rowsRef.current
        .filter((r) => r.status.kind === 'ready' && r.title.trim())
        .map((r) => r.id);
      await run(ids, settings);
      return { started: ids.length, needsTitle: untitled.length };
    },
    [run, running],
  );

  const retryFailed = useCallback(
    async (settings: BulkSettings) => {
      if (running) return;
      const ids = rowsRef.current.filter((r) => r.status.kind === 'failed').map((r) => r.id);
      rowsRef.current = rowsRef.current.map((r) =>
        r.status.kind === 'failed' ? { ...r, status: { kind: 'ready' } } : r,
      );
      setRows(rowsRef.current);
      await run(ids, settings);
    },
    [run, running],
  );

  const reset = useCallback(() => {
    rowsRef.current = [];
    setRows([]);
    setFinished(false);
  }, []);

  const counts = useMemo(() => countRows(rows), [rows]);

  return { rows, running, finished, counts, addFiles, removeRow, removeInvalid, setTitle, start, retryFailed, reset };
}

/**
 * Whether a row is still the person's to change. Decided per row, not per
 * batch: after "2 uploaded, 1 skipped" the two are settled and the one that
 * was sent back for a title is not — it must stay open, and be uploadable
 * once named.
 */
export function rowIsOpen(row: BulkRow): boolean {
  const s = row.status;
  return s.kind === 'checking' || s.kind === 'ready' || s.kind === 'problem' || s.kind === 'failed';
}

/** Whether the row's title is worth an input: it may still be uploaded. */
export function rowTitleEditable(row: BulkRow): boolean {
  const s = row.status;
  return s.kind === 'checking' || s.kind === 'ready' || (s.kind === 'problem' && s.reason === NEEDS_TITLE);
}

function countRows(rows: BulkRow[]) {
  // needsTitle is the one problem the person can fix in place; it is
  // counted within problems and also on its own, so the page can say so.
  const c = { total: rows.length, checking: 0, ready: 0, problems: 0, needsTitle: 0, inFlight: 0, done: 0, failed: 0 };
  for (const r of rows) {
    switch (r.status.kind) {
      case 'checking': c.checking += 1; break;
      case 'ready': c.ready += 1; break;
      case 'problem':
        c.problems += 1;
        if (r.status.reason === NEEDS_TITLE) c.needsTitle += 1;
        break;
      case 'done': c.done += 1; break;
      case 'failed': c.failed += 1; break;
      default: c.inFlight += 1;
    }
  }
  return c;
}
